import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mic, Radio, Youtube, Headphones, BookOpen, Globe } from 'lucide-react';
import Section from './Section';

const SERVICES = [
  { icon: Radio, t: 'Podcast management', d: 'Record once a week. We edit, publish, write show notes and grow your show on every platform.', to: '/podcast-management', cta: 'Audio & Co.\u2019s approach to podcast growth', id: 'management' },
  { icon: Mic, t: 'Podcast production', d: 'You record. We edit, mix, master, write show notes and publish. From \u00a3120 per episode.', to: '/podcast-production', cta: 'See podcast production', id: 'podcast' },
  { icon: Youtube, t: 'Podcast to YouTube + Shorts', d: 'Every episode becomes a YouTube video plus daily Shorts. Edited, captioned and uploaded.', to: '/podcast-to-youtube', cta: 'See podcast to YouTube', id: 'youtube' },
  { icon: Headphones, t: 'Audiobook production', d: 'Narration, studio-quality recording and ACX-ready mastering for authors. From \u00a360 per hour.', to: '/audiobook-production', cta: 'See audiobook production', id: 'authors' },
  { icon: BookOpen, t: 'Book publishing', d: 'Done-for-you Amazon self-publishing: formatting, cover, ISBN and launch. From \u00a37.50 per 1,000 words.', to: '/self-publishing', cta: 'See self-publishing', id: 'publishing' },
  { icon: Globe, t: 'Business websites built for SEO', d: 'Your podcast, books, YouTube and blog in one searchable home on your domain. From \u00a3495 one-off.', to: '/authority-hub', cta: 'See business websites', id: 'websites' },
];

export const ServicesGrid = () => (
  <Section bg="white" testId="home-starting-point">
    <div className="max-w-3xl mb-8">
      <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Our full service offering at Audio &amp; Co.</p>
      <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">
        Podcast management, book publishing, audiobooks and SEO websites.
      </h2>
      <p className="text-[#475569] mt-3 text-lg">Start with one service or have the whole lot done for you.</p>
    </div>
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {SERVICES.map(({ icon: Icon, t, d, to, cta, id }) => (
        <Link key={id} to={to} className="group bg-white border border-[#E2E8F0] rounded-[14px] p-6 hover:border-[#BC516F] hover:shadow-[0_10px_30px_rgba(30,42,68,0.08)] transition-[border-color,box-shadow] duration-300" data-testid={`starting-point-${id}`}>
          <Icon className="w-6 h-6 text-[#BC516F]" />
          <h3 className="font-display font-semibold text-[#1E2A44] text-xl mt-3">{t}</h3>
          <p className="text-[#475569] mt-2 text-[15px] leading-relaxed">{d}</p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-[#BC516F] font-semibold text-sm">{cta} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" /></span>
        </Link>
      ))}
    </div>
  </Section>
);

const WHO = [
  { t: 'Coaches', goal: 'Want a steady flow of discovery calls without living on social media.', link: { to: '/authority-hub', label: 'A complete digital authority system for coaches' }, id: 'coaches' },
  { t: 'Consultants', goal: 'Want their expertise to show up when clients search, not just when they pitch.', link: { to: '/authority-hub', label: 'Turn your podcast into a client-generating asset' }, id: 'consultants' },
  { t: 'Authors', goal: 'Want a published book, an audiobook and a platform that keeps selling it.', link: { to: '/case-studies/dimple-method', label: 'Podcast and website for authors' }, id: 'authors' },
];

const STATS = [
  { n: '16 years', l: 'in business' },
  { n: 'Top 10%', l: 'worldwide for a client podcast in 2 months' },
  { n: '211', l: 'episodes shipped for one client' },
  { n: '2', l: 'Amazon bestselling client authors' },
];

export const WhoWeWorkWith = () => (
  <Section bg="bg" testId="home-who">
    <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Who we work with</p>
    <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2 max-w-3xl">Trusted by UK coaches, consultants and authors.</h2>
    <div className="mt-10 grid md:grid-cols-3 gap-5">
      {WHO.map((w) => (
        <div key={w.id} className="bg-white border border-[#E2E8F0] rounded-[14px] p-7" data-testid={`home-who-${w.id}`}>
          <h3 className="font-display font-semibold text-[#1E2A44] text-2xl">{w.t}</h3>
          <p className="text-[#475569] mt-2 leading-relaxed">{w.goal}</p>
          <Link to={w.link.to} className="mt-4 inline-flex items-center gap-1.5 text-[#BC516F] hover:text-[#A7468A] font-semibold text-sm">{w.link.label} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      ))}
    </div>
    <dl className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="home-trust-stats">
      {STATS.map((s) => (
        <div key={s.l} className="rounded-[14px] bg-[#1E2A44] p-5">
          <dt className="sr-only">{s.l}</dt>
          <dd className="font-display font-bold text-2xl sm:text-3xl text-transparent bg-clip-text bg-gradient-to-r from-[#E7A3C4] to-[#BC516F]">{s.n}</dd>
          <dd className="text-white/75 text-sm mt-1">{s.l}</dd>
        </div>
      ))}
    </dl>
    <p className="mt-8 text-[#475569]">
      <Link to="/results" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">See what Audio &amp; Co. clients achieve</Link>, or{' '}
      <Link to="/book" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">discover how Audio &amp; Co. can help</Link> on a free 30-minute call.
    </p>
  </Section>
);

const SERVICE_LINKS = [
  ['Podcast management', '/podcast-management'],
  ['Book publishing', '/self-publishing'],
  ['Audiobooks', '/audiobook-production'],
  ['YouTube', '/podcast-to-youtube'],
  ['SEO business websites', '/authority-hub'],
];

export const HeroServiceLine = () => (
  <p className="mt-5 max-w-2xl text-sm text-[#475569]" data-testid="home-hero-service-line">
    <span className="font-semibold text-[#1E2A44]">Audio &amp; Co. is a UK team for coaches, consultants and authors:</span>{' '}
    {SERVICE_LINKS.map(([l, to], i) => (
      <React.Fragment key={to}>
        <Link to={to} className="text-[#A7468A] hover:text-[#BC516F] font-medium underline-offset-2 hover:underline">{l}</Link>
        {i < SERVICE_LINKS.length - 1 ? <span className="text-[#94A3B8]"> · </span> : '.'}
      </React.Fragment>
    ))}
  </p>
);

const FLOW = [
  { icon: Mic, t: 'Podcast', d: 'One weekly recording builds trust in your voice.' },
  { icon: Youtube, t: 'YouTube', d: 'The same recording becomes long-form video and Shorts.' },
  { icon: BookOpen, t: 'Book + audiobook', d: 'Your best ideas, published and narrated, prove depth.' },
  { icon: Globe, t: 'Business website', d: 'Everything lands on your domain, linked by topic and found on Google.' },
];

export const CompoundSection = () => (
  <Section bg="white" testId="home-compound">
    <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">How Audio &amp; Co. transforms your expert content</p>
    <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2 max-w-3xl">One idea, four channels, credibility that compounds.</h2>
    <p className="text-[#475569] mt-4 max-w-3xl leading-relaxed">
      Each channel on its own is easy to ignore. Together they reinforce each other: a listener finds your video, a viewer buys your book,
      a reader searches your name and lands on your website. Every piece points to the next, so authority builds week after week.
    </p>
    <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 relative">
      {FLOW.map(({ icon: Icon, t, d }, i) => (
        <li key={t} className="relative bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-6">
          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#A7468A] to-[#BC516F] flex items-center justify-center"><Icon className="w-5 h-5 text-white" /></span>
          <h3 className="font-display font-semibold text-[#1E2A44] text-lg mt-4">{t}</h3>
          <p className="text-[#475569] mt-1.5 text-[15px] leading-relaxed">{d}</p>
          {i < FLOW.length - 1 && <ArrowRight aria-hidden className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#BC516F] z-10" />}
        </li>
      ))}
    </ol>
    <p className="mt-8 text-[#475569]">
      Want to <Link to="/podcast-management#authority-podcasting" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">build your thought leadership with a podcast</Link>? See it working in two full builds: <Link to="/case-studies/thrive-katie-bell" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">podcast and website for coaches</Link> and{' '}
      <Link to="/case-studies/dimple-method" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">podcast and website for authors</Link>.
    </p>
  </Section>
);

export const FounderStrip = () => (
  <Section bg="bg" testId="home-founder">
    <div className="grid md:grid-cols-[220px_1fr] gap-8 items-center">
      <img src="/images/jo-day.jpg" alt="Jo Day, founder of Audio & Co., UK specialist in podcast management, book publishing and websites built for SEO" title="Jo Day, Founder | Audio & Co." width="220" height="220" loading="lazy" className="w-44 h-44 md:w-[220px] md:h-[220px] rounded-full object-cover ring-4 ring-white shadow-[0_18px_44px_rgba(30,42,68,0.14)]" />
      <div>
        <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Meet the founder</p>
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">Hi, I&rsquo;m Jo Day.</h2>
        <p className="text-[#475569] mt-4 leading-relaxed max-w-2xl">
          I&rsquo;ve run my own businesses for sixteen years, including Audio &amp; Co.&reg;, PopCrown and Brunchi.shop. Most of our clients come
          from referrals and word of mouth. We do the production, you get the visibility, and you keep running your actual business.
        </p>
        <Link to="/about" className="mt-5 inline-flex items-center gap-1.5 text-[#BC516F] hover:text-[#A7468A] font-semibold" data-testid="home-founder-link">
          Learn more about our founder Jo Day <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </Section>
);
